import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, blogPosts } from "../controller/global";
import { submitLead } from "../controller/leads";
import { Link } from "react-router";
import { Images } from "../controller/Images";
import { FaBolt, FaBrain, FaChevronDown, FaDatabase, FaDiagramProject, FaGears, FaLayerGroup } from "react-icons/fa6";

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// The previous title ran to 70 characters and the description to 189, so
// Google truncated both — a searcher never saw the end of either. These fit
// inside the ~60 and ~160 character limits, lead with the query, and back it
// with specifics from real engagements rather than another round of
// "enterprise solutions" phrasing every competing result also uses.
export const aiIntegrationSEO = {
  title: "AI Integration Services: Add AI Without a Rebuild | InQmise",
  description:
    "AI integration services for systems already in production. Real work: 40+ insurance carriers, live gym access control, retail recommendations.",
  keywords:
    "AI Integration Services, Enterprise AI Integration, Machine Learning Deployment, System Integration AI, Intelligent Automation Services, OpenAI, Claude",
  canonical: "https://inqmisetechnologies.com/ai-integration-services",
  og: {
    title: "AI Integration Services — No Rebuild Required",
    description: "AI integration services for systems already in production, without rebuilding what already works.",
    url: "https://inqmisetechnologies.com/ai-integration-services",
    type: "website",
    image: "https://inqmisetechnologies.com/logo.png",
  },
  twitter: {
    card: "summary_large_image",
    title: "AI Integration Services — No Rebuild Required",
    description: "AI integration services for systems already in production, without rebuilding what already works.",
    image: "https://inqmisetechnologies.com/logo.png",
  },
};

const SERVICES = [
  {
    Icon: FaBrain,
    title: "LLM API Integration",
    desc: "Integrate OpenAI, Claude, and other language models into your applications through production-grade APIs.",
    detail:
      "The API call is the easy part. The real work is everything around it. Prompts are versioned, so a wording change gets reviewed like code. Token budgets stop a runaway loop from becoming a five-figure bill. Retries and fallbacks cover a provider outage. Every response is validated before it reaches your database. The provider sits behind an interface, so switching between OpenAI, Anthropic or open source models is a config change, not a rewrite.",
    link: { to: "/llm-integration-services", text: "LLM integration services in depth" },
  },
  {
    Icon: FaGears,
    title: "Machine Learning Model Deployment",
    desc: "Deploy ML models to production with scaling, monitoring, and continuous optimization built in.",
    detail:
      "A model that performs well in a notebook frequently disappoints in production, because live traffic does not resemble the training set. We deploy behind a versioned endpoint, log inputs and predictions so drift is measurable rather than anecdotal, and set up shadow deployment so a new version runs against real traffic without affecting anyone until its numbers justify the switch. Rollback is a deployment step, not an incident.",
  },
  {
    Icon: FaDatabase,
    title: "Data Pipeline Integration",
    desc: "Build ETL pipelines that feed AI systems with clean, structured, and continuously refreshed data.",
    detail:
      "AI projects usually fail on data, not models. We map the data flow between your systems first. Then we fix records that disagree and agree on one source of truth. Pipelines survive upstream schema changes and never double-count on a replay. Validation and alerts ship with the pipeline, because a quietly stale feed is worse than a broken one.",
  },
  {
    Icon: FaBolt,
    title: "Real-Time AI Processing",
    desc: "Implement low-latency AI inference for real-time decisions inside live workflows.",
    detail:
      "Real time means a latency budget, and the budget is set by the person waiting. We work backwards from that number: what can be precomputed, what can be cached, what must be inferred live, and what the system does when the model is slower than the budget allows. Degrading gracefully to a simpler rule beats making a customer wait. We learned this the hard way integrating entry gates for a multi-location gym chain, where a three-second hesitation at peak hour is not a delay, it is a queue.",
  },
  {
    Icon: FaLayerGroup,
    title: "Legacy System AI Integration",
    desc: "Add AI capabilities to existing legacy systems without a costly, risky full rebuild.",
    detail:
      "The systems worth adding AI to usually cannot stop. So we work alongside them. An adapter layer reads through the interfaces you already have. We move one capability at a time, with a rollback path at every step. If a legacy platform has no usable API, we build one around it instead of changing it. Your system keeps working exactly as it does today.",
  },
  {
    Icon: FaDiagramProject,
    title: "Multi-Model Orchestration",
    desc: "Coordinate multiple AI models to work together for complex, multi-step business logic.",
    detail:
      "Multi-step AI workflows fail differently from single calls: errors compound, one weak step poisons everything downstream, and cost multiplies quietly. We make each step independently observable and independently retryable, define what happens when step three disagrees with step one, and route work to the cheapest model that meets the accuracy bar rather than sending everything to the largest one. Vendor independence matters here too, since routing across providers is only possible if none of them is wired into your business logic. Where those workflows need to act rather than answer, that is agent territory \u2014 see our AI agent development and integration work.",
    link: { to: "/agentic-ai-development", text: "AI agent development and integration" },
  },
];

// "AI integration" is six different projects wearing one name, and buyers
// routinely arrive asking for one while needing another. Search bears this out:
// the queries this page earns impressions for split along exactly these lines.
const INTEGRATION_KINDS = [
  {
    kind: "AI system integration services",
    desc:
      "Wiring AI into the systems that already run your operations \u2014 ERP, CRM, warehouse management, scheduling. The binding constraint is that none of them can stop, so the work is adapters and read-only paths first, modifications much later if at all. This is the most common request we get, and the one where a full rebuild is most often proposed and most often the wrong call.",
  },
  {
    kind: "LLM integration services",
    desc:
      "Putting a large language model to work on one task inside your systems: answering from your documents, extracting data from forms and emails, or triaging customer support. This is specialist work with its own evaluation and cost rules, so it has its own page.",
    link: { to: "/llm-integration-services", text: "See our LLM integration services" },
  },
  {
    kind: "AI API integration services",
    desc:
      "The capability already exists and needs to be wired in properly. Straightforward until production, at which point it is rate limits, retries, token budgets, provider outages and response validation. Most of the engineering lives in the failure paths, which is why an afternoon's prototype and a shippable feature are different projects with different estimates.",
  },
  {
    kind: "AI website integration services",
    desc:
      "Customer-facing AI on your website or app: search that understands intent, customer support that resolves questions instead of deflecting them, and recommendations based on what a visitor is actually doing. The bar is higher than for internal tools. Every failure is public, and a slow answer hurts the customer experience of someone who can simply leave.",
  },
  {
    kind: "Custom AI integration services",
    desc:
      "For when an off-the-shelf tool almost fits and the missing 20% is the part your business actually runs on. In practice this usually means a bespoke layer around a standard model rather than a bespoke model \u2014 the expensive mistake is assuming you need to train something when you need to constrain, validate and route something.",
  },
  {
    kind: "AI tool integration services",
    desc:
      "You are already paying for AI tools that do not talk to each other, or to the systems of record. Connecting what you have is frequently the cheapest useful project available, and it is worth exhausting before commissioning anything new.",
  },
];

// The page's strongest evidence was sitting unlinked: three of the five case
// studies are integration engagements. Titles come from blogPosts so this stays
// in step with the case studies themselves.
const PROOF_SLUGS = [
  { slug: "gym-chain-integration", angle: "Hardware and software as one system: membership state enforced at entry gates and payment kiosks in real time." },
  { slug: "ezrater", angle: "Cross-carrier quote aggregation, solving ACORD interoperability across 40+ insurance carriers." },
  { slug: "ai-style-recommendation", angle: "A recommendation engine integrated into an existing retail platform rather than replacing it." },
];

const PROOF = PROOF_SLUGS
  .map(({ slug, angle }) => {
    const post = blogPosts.find((p) => p.slug === slug);
    return post ? { slug, angle, title: post.title, category: post.category } : null;
  })
  .filter(Boolean);

const BEFORE_AFTER = {
  before: [
    "Manual triage eats hours of your team's day",
    "Customer and product data locked in silos",
    "Decisions wait on end-of-week reports",
    "Every new AI idea means a system rebuild",
  ],
  after: [
    "AI agents triage and route requests instantly",
    "A unified data layer feeds every system",
    "Decisions happen in real time, not next Friday",
    "New AI capability plugs in without touching core systems",
  ],
};

const TECH_GROUPS = [
  { label: "AI/ML Platforms", items: ["OpenAI", "Anthropic Claude", "Google Vertex AI", "AWS SageMaker"] },
  { label: "Data & Storage", items: ["Databricks", "Snowflake", "Pinecone", "Weaviate"] },
  { label: "Infrastructure", items: ["AWS", "Azure", "Google Cloud", "Multi-Cloud"] },
];

const PROCESS_STEPS = [
  { step: "Assessment", desc: "Analyze your systems and evaluate technical feasibility." },
  { step: "Architecture", desc: "Design integration architecture and API contracts up front." },
  { step: "Development", desc: "Build connectors, APIs, and data pipelines." },
  { step: "Validation", desc: "Test model outputs and integration quality before launch." },
  { step: "Deployment", desc: "Roll out with minimal downtime and a rollback plan." },
  { step: "Monitoring", desc: "Track performance and business impact after launch." },
];

const BENEFITS = [
  { title: "Non-Disruptive", desc: "Add AI without rebuilding from scratch." },
  { title: "Fast Deployment", desc: "Go live in weeks, not months." },
  { title: "Lower Risk", desc: "Leverage existing infrastructure and data." },
  { title: "ROI-Focused", desc: "Proven ROI within 3-6 months." },
];

const INDUSTRIES = [
  { industry: "E-Commerce & Retail", desc: "Enterprise AI integration for personalization, inventory forecasting, and support automation." },
  { industry: "Financial Services & Banking", desc: "Large language model integration for document review, fraud detection, and compliance workflows." },
  { industry: "Healthcare", desc: "System integration AI that connects clinical data with decision-support tooling securely." },
  { industry: "SaaS & Technology", desc: "Embed AI features directly into your product without disrupting existing users." },
  { industry: "Manufacturing", desc: "Machine learning deployment for predictive maintenance and supply chain optimization." },
  { industry: "Logistics", desc: "Real-time AI processing for route optimization and demand forecasting." },
];

const CASE_STUDIES = [
  { org: "Legacy ERP Platform", quote: "Live with LLM-powered document processing in 6 weeks — without touching our core codebase." },
  { org: "Multi-Cloud SaaS", quote: "We now route requests across three AI providers without a single line of vendor-specific code." },
  { org: "Regional Bank", quote: "Our real-time fraud-scoring pipeline went live with zero downtime and 99.95% uptime since." },
];

const FAQS = [
  {
    q: "Can AI be integrated into existing legacy systems?",
    a: "Yes. AI integration services enable adding intelligent capabilities to legacy systems without a complete rebuild, using APIs, adapters, and microservices architectures that sit alongside your existing stack.",
  },
  {
    q: "How long does AI integration typically take?",
    a: "Most AI integration services projects take 8-16 weeks for a production-ready system, though focused MVPs can be deployed in 4-6 weeks depending on complexity and data readiness.",
  },
  {
    q: "Which LLM and machine learning providers do you integrate with?",
    a: "OpenAI, Anthropic Claude, Google Gemini and Vertex AI, AWS SageMaker, and open source models such as Llama and Mistral, hosted or on-premise. Most projects need prompt engineering and good retrieval rather than a custom model. We fine-tune models only when testing shows prompting has hit its limit.",
  },
  {
    q: "Will AI integration disrupt our existing systems?",
    a: "No. Enterprise AI integration is designed to be non-disruptive — we build alongside your existing infrastructure with staged rollouts and rollback plans rather than a risky full replacement.",
  },
  {
    q: "How do you handle multiple AI models working together?",
    a: "We build multi-model orchestration layers that route requests to the right model for each task, so you're never locked into a single AI provider.",
  },
  {
    q: "What do AI integration services cost?",
    a: "Four things drive the number: how clean and reachable your data already is, whether the target system exposes a usable API, how many failure paths have to be handled properly, and whether a human stays in the loop. Data readiness is usually the largest single variable \u2014 it is common for the integration itself to be smaller than the work of making the data trustworthy enough to integrate. We scope against those four before quoting rather than pricing by headcount.",
  },
  {
    q: "Do you provide AI integration services outside India?",
    a: "Yes. We are an AI integration services company based in Delhi NCR and work remotely with clients in other time zones, with overlap hours agreed at kickoff. Integration work suits that model well: the artefacts are code, runbooks and dashboards, and the phases that genuinely need live collaboration are concentrated in assessment and validation.",
  },
  {
    q: "Is our data safe during AI integration?",
    a: "Data stays in your own cloud account wherever possible, access is limited to what each component needs, and we use provider options that do not train on your data. Where rules require it, models can run on your own infrastructure.",
  },
  {
    q: "Who owns the code and the AI system you build?",
    a: "You do. Source code, prompts, evaluation sets, trained model weights and documentation are handed over and belong to you, and we sign an NDA before any detailed discussion.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

const AIIntegrationServices = () => {
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = aiIntegrationSEO.title;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = aiIntegrationSEO.description;
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = aiIntegrationSEO.keywords;
    document.head.appendChild(keywords);

    const ogTitle = document.createElement("meta");
    ogTitle.setAttribute("property", "og:title");
    ogTitle.content = aiIntegrationSEO.og.title;
    document.head.appendChild(ogTitle);

    const ogDesc = document.createElement("meta");
    ogDesc.setAttribute("property", "og:description");
    ogDesc.content = aiIntegrationSEO.og.description;
    document.head.appendChild(ogDesc);

    const ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.content = aiIntegrationSEO.og.url;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement("meta");
    ogType.setAttribute("property", "og:type");
    ogType.content = aiIntegrationSEO.og.type;
    document.head.appendChild(ogType);

    const ogImage = document.createElement("meta");
    ogImage.setAttribute("property", "og:image");
    ogImage.content = aiIntegrationSEO.og.image;
    document.head.appendChild(ogImage);

    const twitterCard = document.createElement("meta");
    twitterCard.name = "twitter:card";
    twitterCard.content = aiIntegrationSEO.twitter.card;
    document.head.appendChild(twitterCard);

    const twitterTitle = document.createElement("meta");
    twitterTitle.name = "twitter:title";
    twitterTitle.content = aiIntegrationSEO.twitter.title;
    document.head.appendChild(twitterTitle);

    const twitterDesc = document.createElement("meta");
    twitterDesc.name = "twitter:description";
    twitterDesc.content = aiIntegrationSEO.twitter.description;
    document.head.appendChild(twitterDesc);

    const twitterImage = document.createElement("meta");
    twitterImage.name = "twitter:image";
    twitterImage.content = aiIntegrationSEO.twitter.image;
    document.head.appendChild(twitterImage);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", aiIntegrationSEO.canonical);

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI Integration Services",
      "description": aiIntegrationSEO.description,
      "url": aiIntegrationSEO.canonical,
      "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
      "availableLanguage": ["en-IN"],
      "serviceType": "AI Integration Services",
    });
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": "AI Integration Services", "item": aiIntegrationSEO.canonical },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Please tell us about your systems.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO — same split panel shape as the other AI/service pages */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Seamless integration · non-disruptive by design</p>
          <h1>AI and LLM integration services that plug into your existing software.</h1>
          <p className="ea-lede">
            Our AI integration services add machine learning, generative AI and automation to the applications your team already uses. Nothing gets rebuilt. Each integration sits beside your current systems, so the business keeps running while the new capability goes live.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your AI integration">
              Start integration ↗
            </button>
            <a className="ea-text-link" href="#services">See what we integrate</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / INTEGRATION HUB <b>LIVE</b></div>
          <h2>Same systems, a different day-to-day.</h2>
          <div className="ea-query"><small>BEFORE</small>{BEFORE_AFTER.before[0]}</div>
          <div className="ea-query"><small>AFTER</small>{BEFORE_AFTER.after[0]}</div>
          <div className="ea-flow"><b>Connect</b><i>→</i><b>Embed</b><i>→</i><b>Integrate</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* BEFORE / AFTER */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">What changes</div>
            <h2>Same team,<br />different day-to-day.</h2>
          </div>
          <p>Integration doesn't just add a feature — it changes how your team works day to day.</p>
        </Reveal>
        <div className="ai-diff">
          <Reveal as="div" reduceMotion={reduceMotion} className="ai-diff-col">
            <h3>Without AI integration</h3>
            <ul>
              {BEFORE_AFTER.before.map((item) => (
                <li key={item}><span className="ai-diff-mark">✕</span><span>{item}</span></li>
              ))}
            </ul>
          </Reveal>
          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ai-diff-col ai-diff-after">
            <h3>With InQmise integration</h3>
            <ul>
              {BEFORE_AFTER.after.map((item) => (
                <li key={item}><span className="ai-diff-mark">✓</span><span>{item}</span></li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* SERVICES — lattice grid */}
      <section className="ea-wrap ea-section" id="services">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">What we build</div>
            <h2>Integration capabilities,<br />any stack.</h2>
          </div>
          <p>Full-spectrum AI integration for any platform, technology stack, or business requirement.</p>
        </Reveal>
        <div className="ai-services-grid">
          {SERVICES.map((item, i) => (
            <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ai-service-cell" key={item.title}>
              <div className="ea-icon"><item.Icon aria-hidden="true" /></div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
              <p className="ai-service-detail">{item.detail}</p>
              {item.link && (
                <Link className="ea-text-link" to={item.link.to}>
                  {item.link.text} &#8599;
                </Link>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHICH KIND — orients a buyer who knows they want "AI integration"
          but not which of the five jobs that phrase covers. */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Before you scope it</div>
            <h2>Which kind of AI<br />integration do you need?</h2>
          </div>
          <p>Six different projects share one name. Being precise about which one you are buying is the cheapest risk reduction available.</p>
        </Reveal>
        <div className="aa-pill-grid">
          {INTEGRATION_KINDS.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ai-industry-card" key={item.kind} style={{ flex: "1 1 300px" }}>
              <h3>{item.kind}</h3>
              <p>{item.desc}</p>
              {item.link && (
                <Link className="ea-text-link" to={item.link.to}>
                  {item.link.text} &#8599;
                </Link>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* REAL ENGAGEMENTS — links from this page to the case studies that
          demonstrate the capabilities above. */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <div className="ea-section-head">
            <span>PROOF</span>
            <h2>Integrations we&rsquo;ve<br />actually shipped.</h2>
            <p>Three engagements where the integration work was the hard part, written up in detail.</p>
          </div>
          <div className="ai-proof-grid">
            {PROOF.map((item, i) => (
              <Reveal as="article" i={(i % 3) + 1} reduceMotion={reduceMotion} className="ai-proof-card" key={item.slug}>
                <span className="ai-proof-cat">{item.category}</span>
                <h3>
                  <Link to={`/case-studies/${item.slug}`}>{item.title}</Link>
                </h3>
                <p>{item.angle}</p>
                <Link className="ea-text-link" to={`/case-studies/${item.slug}`}>
                  Read the case study &#8599;
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TECH GROUPS */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Under the hood</div>
              <h2>Works with<br />your stack.</h2>
            </div>
            <p>Machine learning deployment and integration across every major AI and data platform.</p>
          </Reveal>
          <div className="ai-tech-rows">
            {TECH_GROUPS.map((group, i) => (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ai-tech-row" key={group.label}>
                <span className="ai-tech-row-label">{group.label}</span>
                <div className="cs-tags-row">
                  {group.items.map((tech) => <span key={tech} className="cs-tag">{tech}</span>)}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS — horizontal flowing step-chips */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">A systematic methodology</div>
            <h2>Six steps,<br />zero surprises.</h2>
          </div>
          <p>The same integration approach whether it's one API or a full multi-model pipeline.</p>
        </Reveal>
        <div className="ai-steps-flow">
          {PROCESS_STEPS.map((item, i) => (
            <React.Fragment key={item.step}>
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ai-step-chip">
                <span className="ai-step-chip-num">{i + 1}</span>
                <h3>{item.step}</h3>
                <p>{item.desc}</p>
              </Reveal>
              {i < PROCESS_STEPS.length - 1 && <span className="ai-step-arrow">→</span>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Why now</div>
            <h2>Integrate today,<br />not after a rebuild.</h2>
          </div>
          <p>The case for adding AI now instead of waiting for a full platform rebuild.</p>
        </Reveal>
        <div className="aa-stats-strip" style={{ marginTop: 0 }}>
          {BENEFITS.map((b, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-stat" key={b.title}>
              <strong style={{ fontSize: "var(--t-md)" }}>{b.title}</strong>
              <span>{b.desc}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDUSTRIES — left-accent cards */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Where we've delivered</div>
            <h2>Tailored to your<br />industry's constraints.</h2>
          </div>
          <p>AI integration services tailored to the compliance and data requirements of your industry.</p>
        </Reveal>
        <div className="aa-pill-grid">
          {INDUSTRIES.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ai-industry-card" key={item.industry} style={{ flex: "1 1 260px" }}>
              <h3>{item.industry}</h3>
              <p>{item.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CASE STUDIES — stacked quote list */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Client success stories</div>
            <h2>Told in their<br />own words.</h2>
          </div>
          <p>Representative outcomes from recent AI integration services engagements.</p>
        </Reveal>
        <div className="ai-quotes">
          {CASE_STUDIES.map((item, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ai-quote" key={item.org}>
              <span className="ai-quote-mark">"</span>
              <div>
                <p>{item.quote}</p>
                <span>{item.org}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Frequently asked questions about AI integration services.</h2>
            <p>Short answers to the questions we are usually asked before an engagement begins.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? -1 : i)}>
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">Ready to integrate</div>
            <h2 id="contact-heading">Tell us about the systems you're working with.</h2>
            <p>Our AI integration services team will get back to you within one business day.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>SCHEDULE A CONSULTATION</span>
            <h3>Integrate with confidence.</h3>
            <p>Tell us what you're trying to connect and we'll help you scope it.</p>

            <form onSubmit={handleContactSubmit} aria-label="AI integration services project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="ai-int-fullname">Full Name</label>
                <input
                  id="ai-int-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "ai-int-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="ai-int-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="ai-int-phone">Phone Number (optional)</label>
                  <input
                    id="ai-int-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "ai-int-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="ai-int-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="ai-int-email">Email</label>
                  <input
                    id="ai-int-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "ai-int-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="ai-int-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="ai-int-subject">Subject</label>
                <input
                  id="ai-int-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., Integrate Claude into our support tool"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "ai-int-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="ai-int-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="ai-int-description">Project Details</label>
                <textarea
                  id="ai-int-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "ai-int-description-error" : undefined}
                />
                {contactErrors.description && <p id="ai-int-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Schedule a consultation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default AIIntegrationServices;
